'use client';

import { useState, useEffect } from 'react';

/**
 * Maps IANA timezone identifiers to ISO 3166-1 alpha-2 country codes.
 * Covers common timezones; falls back to "CH" for unmapped zones.
 */
const TIMEZONE_COUNTRY_MAP: Record<string, string> = {
  // Europe
  'Europe/Zurich': 'CH',
  'Europe/Berlin': 'DE',
  'Europe/Vienna': 'AT',
  'Europe/London': 'GB',
  'Europe/Paris': 'FR',
  'Europe/Rome': 'IT',
  'Europe/Madrid': 'ES',
  'Europe/Amsterdam': 'NL',
  'Europe/Brussels': 'BE',
  'Europe/Stockholm': 'SE',
  'Europe/Oslo': 'NO',
  'Europe/Copenhagen': 'DK',
  'Europe/Helsinki': 'FI',
  'Europe/Warsaw': 'PL',
  'Europe/Prague': 'CZ',
  'Europe/Budapest': 'HU',
  'Europe/Bucharest': 'RO',
  'Europe/Sofia': 'BG',
  'Europe/Athens': 'GR',
  'Europe/Lisbon': 'PT',
  'Europe/Dublin': 'IE',
  'Europe/Zagreb': 'HR',
  'Europe/Ljubljana': 'SI',
  'Europe/Bratislava': 'SK',
  'Europe/Tallinn': 'EE',
  'Europe/Riga': 'LV',
  'Europe/Vilnius': 'LT',
  'Europe/Luxembourg': 'LU',
  'Europe/Malta': 'MT',
  'Europe/Nicosia': 'CY',
  'Europe/Istanbul': 'TR',
  'Europe/Moscow': 'RU',
  'Europe/Kiev': 'UA',
  'Europe/Belgrade': 'RS',
  // Americas
  'America/New_York': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Los_Angeles': 'US',
  'America/Anchorage': 'US',
  'Pacific/Honolulu': 'US',
  'America/Phoenix': 'US',
  'America/Detroit': 'US',
  'America/Indiana/Indianapolis': 'US',
  'America/Toronto': 'CA',
  'America/Vancouver': 'CA',
  'America/Edmonton': 'CA',
  'America/Winnipeg': 'CA',
  'America/Halifax': 'CA',
  'America/Montreal': 'CA',
  'America/Mexico_City': 'MX',
  'America/Sao_Paulo': 'BR',
  'America/Argentina/Buenos_Aires': 'AR',
  'America/Santiago': 'CL',
  'America/Bogota': 'CO',
  'America/Lima': 'PE',
  // Asia-Pacific
  'Asia/Tokyo': 'JP',
  'Asia/Shanghai': 'CN',
  'Asia/Hong_Kong': 'HK',
  'Asia/Seoul': 'KR',
  'Asia/Singapore': 'SG',
  'Asia/Taipei': 'TW',
  'Asia/Bangkok': 'TH',
  'Asia/Jakarta': 'ID',
  'Asia/Kolkata': 'IN',
  'Asia/Karachi': 'PK',
  'Asia/Dubai': 'AE',
  'Asia/Riyadh': 'SA',
  'Asia/Tel_Aviv': 'IL',
  'Asia/Dhaka': 'BD',
  // Oceania
  'Australia/Sydney': 'AU',
  'Australia/Melbourne': 'AU',
  'Australia/Brisbane': 'AU',
  'Australia/Perth': 'AU',
  'Pacific/Auckland': 'NZ',
  // Africa
  'Africa/Johannesburg': 'ZA',
  'Africa/Lagos': 'NG',
  'Africa/Cairo': 'EG',
  'Africa/Nairobi': 'KE',
};

function getCountryFromTimezone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TIMEZONE_COUNTRY_MAP[tz] || null;
  } catch {
    return null;
  }
}

/**
 * Detects the user's country using multiple strategies:
 * 1. Server-provided geo header (Vercel/Cloudflare) via API call
 * 2. Browser timezone → country mapping (works offline, no permissions needed)
 * 3. Locale-based fallback
 * 4. Default: "CH"
 */
export function useDetectedCountry(locale?: string, serverHint?: string): string {
  const [country, setCountry] = useState(() => {
    // Start with the best available synchronous guess
    const tzCountry = typeof window !== 'undefined' ? getCountryFromTimezone() : null;
    return serverHint || tzCountry || 'CH';
  });

  useEffect(() => {
    // Try geo-header API for authoritative answer (works on Vercel/Cloudflare)
    let cancelled = false;

    fetch('/api/places/detect-country')
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data.country) {
          setCountry(data.country);
        }
      })
      .catch(() => {
        // API failed — timezone-based value is already set
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return country;
}
